import { withRouter } from 'next/router';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { get, isEmpty, isEqual, noop, omit } from 'lodash';
import moment from 'moment';
import type { BookingEngineEntry } from 'Store/booking/constants';
import { BOOKING_ENGINE_ENTRIES } from 'Store/booking/constants';
import { BOOKING_ENGINE_FORM_SELECTOR } from 'Components/widgets/booking-engine-form/constants';
import { NAVIGATION_OFFSET_SELECTOR } from 'Components/widgets/navigation/Navigation.constants';
import {
  PRICE_COUNTER_PROPOSAL_SELLER_THRESHOLD,
  PRICE_COUNTER_PROPOSAL_THRESHOLD,
} from 'Store/booking/proposals/constants';
import { PRICE_REMOTE_STICKY_WRAPPER_SELECTOR } from 'Components/widgets/price-remote/constants';
import * as routeBuilder from 'Routes/routeBuilder';
import { hasErroredProposals } from 'Utils/proposal/proposalUtils';
import { B2CAppContext } from 'Context/ReactAppContext';
import { isWinterResort } from 'Utils/resort/resortUtils';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { isSeller } from 'Utils/seller/sellerUtils';
import { getAllAttendeesIds } from 'Components/widgets/contact-info/ContactInfoUtils';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import type { FetchProposalsParams } from 'Store/booking/proposals/proposals.action';
import 'Store/booking/proposals/proposals.action';
import { addTranslationKeys } from 'Src/i18n';
import { fetchBookingEngineI18nQuery } from 'Views/booking/BookingEngineI18n.query';
import {
  getBookingEngineUri,
  PAGE_BE_ACCOMMODATION,
  checkIsDepartureDateAvailaible,
} from 'Utils/booking-engine-pages/BookingPagesUtils';
import { AlternativeProposalsModal } from 'Components/widgets/alternative-proposals-modal/AlternativeProposalsModal';
import { BookingCriteriaContainer } from 'Components/widgets/booking-criteria/BookingCriteria.container';
import { BookingErrors } from 'Components/widgets/booking-errors/BookingErrors';
import { PriceCalendar } from 'Components/widgets/booking-form/price-calendar/PriceCalendar';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
export type BookingRedirectParams = {
  adults: number | null | undefined;
  children: number | null | undefined;
  birthdays: Array<string> | null | undefined;
  duration: number | null | undefined;
  departureDate: number | null | undefined;
  arrivalDate: number | null | undefined;
  resortId: string | null | undefined;
  transport: string | null | undefined;
};
type Props = {
  adultsNumber: number;
  arrivalDate: number | null | undefined;
  // parseInt de "YYYYMMDD",
  selectedResort: number | null | undefined;
  bookingConfig: {
    enableTransfer: boolean | null | undefined;
    form: {
      title: string | null | undefined;
      dateFormat: string | null | undefined;
      dateValidate: string | null | undefined;
      selectDuration: string | null | undefined;
      durationTitle: string | null | undefined;
      legalNotice: string;
      selectChildrenLabels: any | null | undefined;
      selectAdultsLabels: any | null | undefined;
      errorMaxGuestsLabel: string | null | undefined;
    };
    priceCalendar: {
      offers: {
        urgentisation: {
          label: string;
          rateToDisplay: number;
        };
      };
    };
  };
  bookingCriteria: {
    initialNumberOfChildren: number | null | undefined;
    initialNumberOfAdults: number | null | undefined;
    initialDuration?: number;
    durations?: Array<number>;
    departureCities: Array<{
      name: string;
      value: string;
    }>;
    offsetSell: number;
  };
  bookingEngineEntry: BookingEngineEntry;
  checkPriceLabel: string | null | undefined;
  clearProposals: () => void;
  config: CMSContent;
  endLoading: () => void;
  startLoading: () => void;
  cmsPrice: any | null | undefined;
  cmsStartingPrice: any | null | undefined;
  dataSelector: string;
  departureDate: number | null | undefined;
  // parseInt de "YYYYMMDD"
  departureCity: string;
  duration: number;
  i18n: any;
  kids: Array<any>;
  package: any | null | undefined;
  proposals: Array<Proposal>;
  queryParams: Record<string, any>;
  selectedProposalId: number | null | undefined;
  selectProposal: (arg0: any, arg1: any) => void;
  pushImage: string;
  renderErrors: (arg0: any, arg1: Array<any>, arg2: boolean) => Node | null | undefined;
  resort: Product;
  router: any;
  transfer: any | null | undefined;
  transport: any | null | undefined;
  travelAdvisorConfig: any | null | undefined;
  updateTripDateCriteria: (arg0: any) => void;
  setDepartureDate: typeof setDepartureDate;
  clearSelectedProposal: (arg0: any) => void;
  clearProposals: (arg0: any) => void;
  version: 'a' | 'b' | 'ab';
  viewMorePackages:
    | {
        label: string | null | undefined;
        visible: boolean | null | undefined;
      }
    | null
    | undefined;
  history: RouterHistory;
  tripInformation: TripInformation;
  fetchProposalsAndRedirect: (
    arg0: FetchProposalsParams,
    arg1: AppContext,
    router: any,
  ) => Promise<any>;
  prepareBe: (arg0: any) => void;
  setCms: (arg0: any, arg1: any) => void;
  setAttendees: (arg0: BookingAttendeesType) => void;
  attendees: BookingAttendeesType;
  suggestions: BookingSuggestions;
};

function BookingEngineForm({
  clearProposals = noop,
  kids = [],
  i18n: i18nProp = null,
  dataSelector = 'BookingEngineForm',
  // @ts-expect-error TODO(flow-migration)
  fetchProposalsAndRedirect = noop,
  // @ts-expect-error TODO(flow-migration)
  suggestions = { dates: [], resorts: [] },
  adultsNumber,
  arrivalDate,
  bookingConfig,
  bookingCriteria,
  bookingEngineEntry,
  cmsPrice,
  cmsStartingPrice,
  departureDate,
  departureCity,
  duration,
  pushImage,
  config,
  proposals,
  queryParams,
  resort,
  router,
  travelAdvisorConfig,
  tripInformation,
  updateTripDateCriteria,
  setDepartureDate: setDepartureDateAction,
  clearSelectedProposal,
  endLoading,
  attendees,
  setCms,
  setAttendees,
  prepareBe,
  ...restProps
}: Props) {
  const context = useContext(B2CAppContext);
  const bookingCriteriaNode = useRef<any>(null);

  const [showModal, setShowModal] = useState(true);
  const [shouldFetchProposals, setShouldFetchProposals] = useState(false);
  const [isDepartureDateAvailaible, setIsDepartureDateAvailaible] = useState(true);

  const prevProposalsRef = useRef(proposals);
  const prevSuggestionsRef = useRef(suggestions);
  const prevResortIdRef = useRef(resort.id);

  function updateIsDepartureDateAvailaible(date: number | null | undefined, offsetSell: number) {
    setIsDepartureDateAvailaible(!checkIsDepartureDateAvailaible(String(date), offsetSell));
  }

  function scrollToBookingEngineEntryPoint() {
    setTimeout(() => {
      const { PRICE_CALENDAR, PRICE_REMOTE } = BOOKING_ENGINE_ENTRIES;
      const bookingEngineFormSelector = document.querySelector(BOOKING_ENGINE_FORM_SELECTOR);
      const priceRemoteSelector = document.querySelector(PRICE_REMOTE_STICKY_WRAPPER_SELECTOR);

      if (bookingEngineEntry === PRICE_CALENDAR && bookingEngineFormSelector) {
        bookingEngineFormSelector.scrollIntoView();
      } else if (bookingEngineEntry === PRICE_REMOTE && priceRemoteSelector) {
        priceRemoteSelector.scrollIntoView();
      }
    }, 0);
  }

  function hasCounterProposals() {
    const proposal = proposals[0];
    const { dates, durations, resorts } = suggestions;
    return (
      dates.length > 0 ||
      durations.length > 0 ||
      resorts.length > 0 ||
      (!isEmpty(proposal) && proposal?.price?.gap >= PRICE_COUNTER_PROPOSAL_THRESHOLD)
    );
  }

  function canDisplayCounterProposals() {
    return (
      proposals[0]?.price?.gap >= PRICE_COUNTER_PROPOSAL_THRESHOLD ||
      suggestions.dates.length > 0 ||
      suggestions.durations.length > 0 ||
      suggestions.resorts.length > 0
    );
  }

  function checkLoading() {
    if (
      suggestions.dates.length > 0 ||
      suggestions.durations.length > 0 ||
      suggestions.resorts.length > 0 ||
      proposals.some(({ id }) => !id) ||
      proposals.some((proposal) => proposal.price.gap > PRICE_COUNTER_PROPOSAL_THRESHOLD)
    ) {
      endLoading();
    }
  }

  function handleSetShouldFetchProposals() {
    const { fetch } = queryParams;
    const shouldFetch = Boolean(fetch && `${fetch}` === '1');
    return () => {
      setShouldFetchProposals(shouldFetch);
    };
  }

  function handleDepartureDate(day: any | null | undefined = null) {
    const newDepartureDate = moment(String(day?.id)).valueOf();

    if (departureDate !== day?.id) {
      setDepartureDateAction(newDepartureDate);
    }

    updateTripDateCriteria({ day });
  }

  function onFormSubmit(event: React.FormEvent) {
    event.preventDefault();
  }

  function getPackages(newArrivalDate: string): Promise<any> {
    const fetchParams = {
      productId: resort.id,
      departureCity,
      duration,
      isJoyView: resort.isJoyview,
      isUrbanOasis: resort.isUrbanOasis,
      globalLoaderLabel: context.isSeller
        ? i18nProp.booking.partners.form.proposalsLoaderLabel
        : i18nProp.booking.form.proposalsLoaderLabel,
      ...(isSeller(context) ? getAllAttendeesIds(tripInformation) : null),
      newArrivalDate,
      bookingEngineEntry: BOOKING_ENGINE_ENTRIES.PRICE_CALENDAR,
    };
    return fetchProposalsAndRedirect(fetchParams, context, router);
  }

  function redirectToBE(newArrivalDate: string) {
    setShowModal(false);
    getPackages(newArrivalDate).then(() => {
      setShowModal(true);
    });
  }

  function shouldDisplayModal() {
    return hasCounterProposals() && canDisplayCounterProposals() && showModal;
  }

  async function goToBookingEngine() {
    setShowModal(false);
    const { config: fetchedConfig, legalInformations } = await fetchBookingEngineI18nQuery(context);

    if (fetchedConfig && legalInformations) {
      setCms(fetchedConfig, legalInformations);
      setAttendees(attendees);
    }

    const proposalId = proposals[0].id;
    prepareBe({
      resort,
      proposals: {
        proposals,
        resort,
      },
      proposalId,
    });
    router.push(
      getBookingEngineUri(
        PAGE_BE_ACCOMMODATION,
        proposals,
        resort,
        proposalId,
        omit(router.query, ['dynamicPage']),
      ),
    );
  }

  // componentDidMount
  useEffect(() => {
    clearSelectedProposal();
    clearProposals();
    updateIsDepartureDateAvailaible(departureDate, bookingCriteria.offsetSell);
    setTimeout(() => scrollToBookingEngineEntryPoint(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate: departureDate changed
  useEffect(() => {
    updateIsDepartureDateAvailaible(departureDate, bookingCriteria.offsetSell);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDate]);

  // componentDidUpdate: shouldFetchProposals became true
  useEffect(() => {
    if (shouldFetchProposals && bookingCriteriaNode.current) {
      bookingCriteriaNode.current.onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchProposals]);

  // componentDidUpdate: proposals[0] or suggestions changed
  useEffect(() => {
    const oldProposal = prevProposalsRef.current[0];
    const newProposal = proposals[0];

    if (!isEqual(oldProposal, newProposal) || !isEqual(prevSuggestionsRef.current, suggestions)) {
      checkLoading();

      if (
        hasErroredProposals(proposals) ||
        (hasCounterProposals() && !canDisplayCounterProposals())
      ) {
        setTimeout(() => {
          scrollToElement('#Package-error', 600, `.${NAVIGATION_OFFSET_SELECTOR}`);
        });
      }
    }

    prevProposalsRef.current = proposals;
    prevSuggestionsRef.current = suggestions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals, suggestions]);

  // componentDidUpdate: resort.id changed -> close modal
  useEffect(() => {
    if (prevResortIdRef.current !== resort.id) {
      setShowModal(false);
    }
    prevResortIdRef.current = resort.id;
  }, [resort.id]);

  const failedProposals = hasErroredProposals(proposals);
  const i18n = {
    ...i18nProp,
    adultAge: get({ bookingConfig }, 'bookingConfig.form.adultAge'),
    bookingCriteria,
    childrenBirthdayDateFormatPlaceholder: get(
      { bookingConfig },
      'bookingConfig.form.childrenBirthdayDateFormatPlaceholder',
    ),
    dateFormat: get({ bookingConfig }, 'bookingConfig.form.dateFormat'),
    dayPlaceholder: get({ bookingConfig }, 'bookingConfig.form.dayPlaceholder'),
    monthPlaceholder: get({ bookingConfig }, 'bookingConfig.form.monthPlaceholder'),
    yearPlaceholder: get({ bookingConfig }, 'bookingConfig.form.yearPlaceholder'),
    dateValidate: get({ bookingConfig }, 'bookingConfig.form.dateValidate'),
    durationTitle: get({ bookingConfig }, 'bookingConfig.form.durationTitle'),
    durationOptionsFormat: get({ bookingConfig }, 'bookingConfig.form.selectDuration'),
    selectAdultsLabels: get({ bookingConfig }, 'bookingConfig.form.selectAdultsLabels'),
    selectChildrenLabels: get({ bookingConfig }, 'bookingConfig.form.selectChildrenLabels'),
    legalNotice: get({ bookingConfig }, 'bookingConfig.form.legalNotice'),
    minAgeInformation: get({ resort }, 'resort.minAuthorizedAge.formattedValue'),
    birthdayFormatErrorMessage: get({ bookingConfig }, 'bookingConfig.form.errors.birthdayFormat'),
    adultAgeErrorMessage: get({ bookingConfig }, 'bookingConfig.form.errors.adultChild'),
    skiPassIncludedLabel: get(
      { bookingConfig },
      'bookingConfig.form.reinsurance.skiPassIncludedLabel',
    ),
    skiPassIncludedDescription: get(
      { bookingConfig },
      'bookingConfig.form.reinsurance.skiPassIncludedDescription',
    ),
    kidStayIncludedLabel: get(
      { bookingConfig },
      'bookingConfig.form.reinsurance.kidStayIncludedLabel',
    ),
    didYouKnowLabel: get({ bookingConfig }, 'bookingConfig.form.reinsurance.didYouKnowLabel'),
    kidStayIncludedDescription: get(
      { bookingConfig },
      'bookingConfig.form.reinsurance.kidStayIncludedDescription',
    ),
    minAgeErrorMessage: get({ resort }, 'resort.minAgeErrorMessage.formattedValue'),
    proposalsLoaderLabel: get({ bookingConfig }, 'bookingConfig.form.proposalsLoaderLabel'),
  };
  addTranslationKeys({
    booking: i18nProp.booking,
    common: i18nProp.common,
    package: i18nProp.package,
    priceRemote: i18nProp.priceRemote,
    resort: i18nProp.resort,
    bookingConfig: {
      form: {
        legalNotice: bookingConfig?.form?.legalNotice,
      },
    },
  });
  return (
    <SiteContainer className="bg-lightSand">
      <form className="py-30 lg:py-40" id="booking" onSubmit={onFormSubmit}>
        <h2
          className="air360-replay-ignore mb-20 border-b-1 border-sand pb-10 font-brand text-24 font-bold leading-tight text-black md:text-28 lg:pb-20"
          data-cs-mask
        >
          {bookingConfig?.form?.title}
        </h2>
        <BookingCriteriaContainer
          myRef={bookingCriteriaNode}
          adultOnly={get({ resort }, 'resort.adultOnly')}
          isWinterResort={isWinterResort(resort)}
          adultsNumber={adultsNumber}
          bookingConfig={bookingConfig}
          duration={duration}
          departureDate={departureDate}
          isDepartureDateAvailaible={isDepartureDateAvailaible}
          departureCity={departureCity}
          enableTransfer={get({ bookingConfig }, 'bookingConfig.enableTransfer')}
          kids={kids}
          getPackages={getPackages}
          isJoyView={get({ resort }, 'resort.isJoyview', false)}
          isTransportEnabled={get(restProps, 'package.transport.enableTransport', false)}
          pushImage={pushImage}
          config={config}
          childAge={get({ resort }, 'resort.minAuthorizedAge.value')}
          resort={get({ resort }, 'resort')}
          i18n={i18n}
        >
          <PriceCalendar
            bookingConfig={bookingConfig}
            cmsPrice={cmsPrice}
            cmsStartingPrice={cmsStartingPrice}
            duration={duration}
            didMountCallback={handleSetShouldFetchProposals()}
            isJoyview={!!resort?.isJoyview}
            key={resort?.id}
            offsetSell={bookingCriteria.offsetSell}
            onSelectDate={handleDepartureDate}
            product={resort}
            productId={resort.id}
            selectedDay={departureDate}
            transport={departureCity}
            travelAdvisorConfig={travelAdvisorConfig}
          />
        </BookingCriteriaContainer>

        {!isDepartureDateAvailaible ||
        failedProposals ||
        proposals.some(
          (proposal) =>
            proposal.price &&
            proposal.price.gap >= PRICE_COUNTER_PROPOSAL_SELLER_THRESHOLD &&
            !isSeller(context),
        ) ? (
          <BookingErrors
            className="mt-20 md:mt-30"
            errorCode={proposals.find(({ errorCode }) => !!errorCode)?.errorCode}
          />
        ) : null}
        {shouldDisplayModal() && (
          <AlternativeProposalsModal
            trackingDefaultPayload={{
              categoryGA: 'resort',
              actionGA: 'checkPricesKO',
            }}
            searchCriteria={{
              arrivalDate: String(arrivalDate),
              duration,
              adultsCount: adultsNumber,
              kidsCount: kids.length,
              departureCity,
            }}
            i18n={i18n}
            onClose={clearProposals}
            onSubmit={redirectToBE}
            proposals={proposals}
            suggestions={suggestions}
            resort={resort}
            goToBookingEngine={goToBookingEngine}
            bookingEngineEntryPoint={bookingEngineEntry}
            scrollToBookingEngineEntryPoint={() => scrollToBookingEngineEntryPoint()}
            onSelectNewDuration={({ proposalIds }) => {
              const newQueryParams = {
                ...omit(router.query, ['dynamicPage']),
                proposalIds,
                selectedProposalId: proposalIds[0],
              };
              router.push(
                routeBuilder.buildUrl(
                  'booking',
                  {
                    page: PAGE_BE_ACCOMMODATION,
                  },
                  newQueryParams,
                ),
              );
            }}
          />
        )}
      </form>
    </SiteContainer>
  );
}

export default withRouter<Props>(BookingEngineForm);
